import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import Select from 'react-select';
import { useLazyQuery, useQuery } from '@apollo/client';
import ButtonV2 from 'components/common/ButtonV2';
import { axiosGetAPI } from 'services';
import { authContext } from 'contexts/AuthContext';
import { toast } from 'react-toastify';
import { CITY_BY_STATE, STATES_BY_COUNTRY } from 'pages/MarketListing/MarketListingGraphQL';
import { calculateDistanceAndTransit } from './distanceService';

const INDIA_COUNTRY_ID = '101';
let cachedCityOptions = null;

const DEFAULT_VEHICLES = [
  { id: 'MT_10', name: '10 MT', capacity: '10 Ton' },
  { id: 'MT_15', name: '15 MT', capacity: '15 Ton' },
  { id: 'MT_20', name: '20 MT', capacity: '20 Ton' },
  { id: 'MT_25', name: '25 MT', capacity: '25 Ton' },
  { id: 'MT_30', name: '30 MT', capacity: '30 Ton' },
  { id: 'MT_35', name: '35 MT', capacity: '35 Ton' },
];

/* ------------------------------------------------------------------ */
/* Shared styles — identical to MatrixDetailView                       */
/* ------------------------------------------------------------------ */
const headerStyle = {
  padding: '10px 14px',
  textAlign: 'left',
  fontWeight: 700,
  color: '#344054',
  background: '#f2f4f7',
  borderBottom: '2px solid #e4e7ec',
  borderRight: '1px solid #e4e7ec',
  fontSize: '13px',
  whiteSpace: 'nowrap',
};

const cellStyle = {
  padding: '12px 14px',
  color: '#344054',
  verticalAlign: 'middle',
  fontSize: '13px',
  borderBottom: '1px solid #e9ecef',
  borderRight: '1px solid #e9ecef',
};

const inputStyle = {
  height: '34px',
  border: '1px solid #d0d5dd',
  borderRadius: '6px',
  padding: '0 10px',
  fontSize: '13px',
  color: '#101828',
  width: '100%',
  outline: 'none',
  background: '#fff',
};

const STATUS_STYLE = {
  Pending: { background: '#fafafa', color: '#475467', border: '1px solid #d0d5dd' },
  Approved: { background: '#ecfdf3', color: '#027a48', border: '1px solid #a6f4c5' },
};

const selectStyles = {
  control: (base, state) => ({
    ...base,
    height: '42px',
    minHeight: '42px',
    borderColor: state.isFocused ? '#1d4ed8' : '#d0d5dd',
    boxShadow: state.isFocused ? '0 0 0 3px rgba(29,78,216,0.1)' : 'none',
    borderRadius: '8px',
    fontSize: '14px',
    color: '#101828',
    background: '#fff',
    '&:hover': { borderColor: '#1d4ed8' },
  }),
  placeholder: (base) => ({ ...base, color: '#98a2b3', fontSize: '14px' }),
  singleValue: (base) => ({ ...base, color: '#101828', fontSize: '14px' }),
  option: (base, state) => {
    let backgroundColor = '#fff';
    if (state.isSelected) backgroundColor = '#eff4ff';
    else if (state.isFocused) backgroundColor = '#f8fafc';
    return {
      ...base,
      fontSize: '14px',
      backgroundColor,
      color: state.isSelected ? '#1d4ed8' : '#101828',
      fontWeight: state.isSelected ? 600 : 400,
      cursor: 'pointer',
    };
  },
  menu: (base) => ({
    ...base,
    borderRadius: '8px',
    boxShadow: '0 8px 24px rgba(16,24,40,0.12)',
    zIndex: 99999,
  }),
  menuPortal: (base) => ({ ...base, zIndex: 99999 }),
  indicatorSeparator: () => ({ display: 'none' }),
};

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */
const AddListView = ({ onBack, onSuccess, cityOptions: initialCityOptions }) => {
  const { auth } = useContext(authContext);
  const [vehicles, setVehicles] = useState(DEFAULT_VEHICLES);
  const [loadingVehicles, setLoadingVehicles] = useState(true);

  const [origin, setOrigin] = useState(null);
  const [destination, setDestination] = useState(null);
  const [distance, setDistance] = useState('');
  const [transitTime, setTransitTime] = useState('');

  const [cityOptions, setCityOptions] = useState(initialCityOptions || cachedCityOptions || []);
  const [citiesLoading, setCitiesLoading] = useState(!initialCityOptions && !cachedCityOptions);

  const [rowData, setRowData] = useState(() =>
    Object.fromEntries(
      DEFAULT_VEHICLES.map((v) => [v.id, { amount: '', rate: '', hasExisting: false }])
    )
  );
  const [submitting, setSubmitting] = useState(false);

  // Auto-fetch distance whenever both origin and destination are selected
  useEffect(() => {
    if (!origin || !destination) return;
    if (origin.value === destination.value) return;

    let isMounted = true;
    const autoFetch = async () => {
      try {
        const originName = origin.cityName || origin.label;
        const destName = destination.cityName || destination.label;
        const calculated = await calculateDistanceAndTransit(originName, destName);
        if (isMounted && calculated?.distanceKm) {
          setDistance(String(calculated.distanceKm));
        }
      } catch (err) {
        console.warn('Auto distance calculation failed in AddListView:', err);
      }
    };

    autoFetch();
    return () => {
      isMounted = false;
    };
  }, [origin, destination]);

  // GraphQL queries for cities if not provided
  const { data: statesData, loading: statesLoading } = useQuery(STATES_BY_COUNTRY, {
    variables: { country: INDIA_COUNTRY_ID },
    fetchPolicy: 'cache-first',
    skip: !!cityOptions.length,
  });
  const [fetchCitiesByState] = useLazyQuery(CITY_BY_STATE, { fetchPolicy: 'cache-first' });

  useEffect(() => {
    if (cityOptions.length) {
      cachedCityOptions = cityOptions;
      return undefined;
    }

    const states = statesData?.statesByCountry || [];
    if (!states.length) return undefined;
    let isMounted = true;

    const loadCities = async () => {
      try {
        const cityGroups = await Promise.all(
          states.map(async (state) => {
            const response = await fetchCitiesByState({ variables: { state: state.id } });
            return (response?.data?.citiesByState || []).map((city) => ({
              value: String(city.id),
              label: `${city.cityName} (${state.stateName})`,
              cityName: city.cityName,
            }));
          })
        );
        const options = cityGroups.flat().sort((a, b) => a.cityName.localeCompare(b.cityName));
        cachedCityOptions = options;
        if (isMounted) setCityOptions(options);
      } catch (error) {
        console.error('Error loading cities:', error);
      } finally {
        if (isMounted) setCitiesLoading(false);
      }
    };

    loadCities();
    return () => {
      isMounted = false;
    };
  }, [fetchCitiesByState, statesData, cityOptions.length]);

  // Fetch vehicle sizes from API
  useEffect(() => {
    let isMounted = true;
    const fetchVehicles = async () => {
      try {
        const vehiclesRes = await axiosGetAPI(
          `${process.env.REACT_APP_REST_API_ENDPOINT}delivery-rate/v2/vehicle-sizes`,
          auth
        );
        if (isMounted && vehiclesRes?.isSuccessful && vehiclesRes?.data?.data) {
          const apiVehicles = vehiclesRes.data.data.map((v) => ({
            id: v.value,
            name: v.label,
            capacity: `${v.tonnage} Ton`,
          }));
          setVehicles(apiVehicles);
          setRowData(
            Object.fromEntries(
              apiVehicles.map((v) => [v.id, { amount: '', rate: '', hasExisting: false }])
            )
          );
        }
      } catch (err) {
        console.error('Error fetching vehicle sizes:', err);
      } finally {
        if (isMounted) setLoadingVehicles(false);
      }
    };

    fetchVehicles();
    return () => {
      isMounted = false;
    };
  }, [auth]);

  // Entering Rate multiplies by tonnage * 1000 to calculate TotalAmount
  const handleRateChange = (vehicleId, value) => {
    setRowData((prev) => {
      const tonnage = parseInt(vehicleId.replace(/\D/g, ''), 10) || 10;
      const multiplier = tonnage * 1000;
      const calculatedAmount =
        value !== '' && !Number.isNaN(Number(value)) && multiplier
          ? Math.round(parseFloat(value) * multiplier)
          : '';

      return {
        ...prev,
        [vehicleId]: {
          ...prev[vehicleId],
          rate: value,
          amount: calculatedAmount !== '' ? String(calculatedAmount) : '',
        },
      };
    });
  };

  const handleAdd = async () => {
    if (!origin) {
      toast.error('Please select Origin.');
      return;
    }
    if (!destination) {
      toast.error('Please select Destination.');
      return;
    }
    if (origin.value === destination.value) {
      toast.error('Origin and Destination cannot be the same.');
      return;
    }
    if (!distance) {
      toast.error('Please enter Distance (km).');
      return;
    }
    if (!transitTime) {
      toast.error('Please enter Transit Time.');
      return;
    }

    const hasAnyRate = Object.values(rowData).some((r) => r.rate && parseFloat(r.rate) > 0);
    if (!hasAnyRate) {
      toast.error('Please enter rate for at least one vehicle.');
      return;
    }

    setSubmitting(true);
    try {
      let transitDaysString = `${transitTime} Days`;
      if (typeof transitTime === 'string' && transitTime.trim()) {
        const trimmed = transitTime.trim();
        transitDaysString = trimmed.toLowerCase().includes('day') ? trimmed : `${trimmed} Days`;
      }

      const payload = {
        originCityId: parseInt(origin.value, 10),
        destinationCityId: parseInt(destination.value, 10),
        distanceKm: parseFloat(distance),
        transitTimeDays: transitDaysString,
      };

      vehicles.forEach((vehicle) => {
        const row = rowData[vehicle.id];
        if (row && row.rate && parseFloat(row.rate) > 0) {
          const num = vehicle.id.replace(/\D/g, '');
          if (num) {
            const rateVal = parseFloat(row.rate);
            const tonnage = parseInt(num, 10) || 10;
            const amountVal =
              row.amount && parseFloat(row.amount) > 0
                ? parseFloat(row.amount)
                : Math.round(rateVal * tonnage * 1000);
            payload[`ftl${num}Mt`] = amountVal;
            payload[`perKg${num}Mt`] = rateVal;
          }
        }
      });

      const endpoint = (process.env.REACT_APP_REST_API_ENDPOINT).replace(/\/?$/, '/');
      const response = await axios.post(
        `${endpoint}delivery-rate/v2/route`,
        payload,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${auth?.data?.token}`,
          },
        },
      );

      const resData = response?.data;
      if (resData?.error) {
        toast.error(resData?.message || 'Failed to add route.');
      } else {
        toast.success(resData?.message || 'Route added successfully!');
        if (onSuccess) {
          onSuccess();
        } else if (onBack) {
          onBack();
        }
      }
    } catch (err) {
      console.error('Error adding route:', err);
      const apiMessage =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        'An error occurred while adding route.';
      toast.error(apiMessage);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mx-wrap">
      {/* Origin / Destination / Distance / Transit Time */}
      <div className="sr-filter-card">
        <div className="mx-filter-row">
          <div className="mx-field">
            <label className="sr-label" htmlFor="add-origin">Origin</label>
            <Select
              inputId="add-origin"
              options={cityOptions}
              value={origin}
              onChange={setOrigin}
              placeholder="Select Origin"
              styles={selectStyles}
              isLoading={statesLoading || citiesLoading}
              isClearable
              menuPortalTarget={typeof document === 'undefined' ? null : document.body}
              menuPosition="fixed"
            />
          </div>

          <div className="mx-field">
            <label className="sr-label" htmlFor="add-dest">Destination</label>
            <Select
              inputId="add-dest"
              options={cityOptions}
              value={destination}
              onChange={setDestination}
              placeholder="Select Destination"
              styles={selectStyles}
              isLoading={statesLoading || citiesLoading}
              isClearable
              menuPortalTarget={typeof document === 'undefined' ? null : document.body}
              menuPosition="fixed"
            />
          </div>

          <div className="mx-field">
            <label className="sr-label" htmlFor="add-distance">Distance (km)</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <input
                id="add-distance"
                type="number"
                min="0"
                placeholder="e.g. 1400"
                className="sr-select"
                style={{ height: '42px' }}
                value={distance}
                onChange={(e) => setDistance(e.target.value)}
              />
              <span style={{ color: '#667085', fontSize: '13px', whiteSpace: 'nowrap' }}>km</span>
            </div>
          </div>

          <div className="mx-field">
            <label className="sr-label" htmlFor="add-transit">Transit Time</label>
            <input
              id="add-transit"
              type="text"
              placeholder="e.g. 3 Days"
              className="sr-select"
              style={{ height: '42px' }}
              value={transitTime}
              onChange={(e) => setTransitTime(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Vehicle rate table */}
      <div className="push-notification-container" style={{ padding: 0, gap: 0 }}>
        {/* Title */}
        <div style={{ padding: '12px 16px', borderBottom: '1px solid #e4e7ec', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ fontSize: '14px', fontWeight: 700, color: '#1d2939' }}>
            Enter Rate per Vehicle
          </span>
          <span style={{ fontSize: '12px', color: '#667085' }}>₹ / kg</span>
        </div>

        {/* Table */}
        <div className="push-notification__table-wrap" style={{ borderRadius: 0, border: 0 }}>
          <table className="push-notification__table" style={{ minWidth: 620 }}>
            <thead>
              <tr>
                <th style={{ ...headerStyle, width: 50 }}>S.No.</th>
                <th style={headerStyle}>Vehicle Type</th>
                <th style={{ ...headerStyle, width: 110 }}>Capacity</th>
                <th style={{ ...headerStyle, width: 110 }}>Status</th>
                <th style={{ ...headerStyle, width: 180 }}>Rate+Loading (₹/kg)</th>
                <th style={{ ...headerStyle, width: 180 }}>TotalAmount (₹)</th>
              </tr>
            </thead>
            <tbody>
              {loadingVehicles ? (
                <tr>
                  <td colSpan="6" style={{ textAlign: 'center', padding: '40px', color: '#667085' }}>
                    Loading vehicle types...
                  </td>
                </tr>
              ) : (
                vehicles.map((vehicle, idx) => {
                  const row = rowData[vehicle.id] || {};
                  const statusKey = row.rate ? 'Approved' : 'Pending';
                  const rowBackground = idx % 2 === 0 ? '#fff' : '#fafafa';

                  return (
                    <tr key={vehicle.id} style={{ background: rowBackground }}>
                      {/* S.No. */}
                      <td style={{ ...cellStyle, color: '#667085', textAlign: 'center' }}>
                        {idx + 1}
                      </td>

                      {/* Vehicle name */}
                      <td style={{ ...cellStyle, fontWeight: 600, color: '#1d2939' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          {vehicle.name}
                        </div>
                      </td>

                      {/* Capacity */}
                      <td style={cellStyle}>
                        <span className="sr-trip-vehicle" style={{ fontSize: '12px' }}>
                          {vehicle.capacity}
                        </span>
                      </td>

                      {/* Status */}
                      <td style={cellStyle}>
                        <span
                          style={{
                            display: 'inline-block',
                            borderRadius: '999px',
                            padding: '3px 10px',
                            fontSize: '11px',
                            fontWeight: 700,
                            ...STATUS_STYLE[statusKey],
                          }}
                        >
                          {statusKey}
                        </span>
                      </td>

                      {/* Rate+Loading input (editable, triggers amount multiplication) */}
                      <td style={cellStyle}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                          <span style={{ color: '#667085', fontSize: '13px' }}>₹</span>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            placeholder="e.g. 10.50"
                            aria-label={`Rate for ${vehicle.name}`}
                            style={{ ...inputStyle, background: '#fff' }}
                            value={row.rate || ''}
                            onChange={(e) => handleRateChange(vehicle.id, e.target.value)}
                          />
                          <span style={{ color: '#667085', fontSize: '12px', whiteSpace: 'nowrap' }}>
                            /kg
                          </span>
                        </div>
                      </td>

                      {/* TotalAmount (auto-calculated from rate * tonnage * 1000, readonly) */}
                      <td style={cellStyle}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                          <span style={{ color: '#667085', fontSize: '13px' }}>₹</span>
                          <input
                            type="number"
                            min="0"
                            placeholder="Auto"
                            aria-label={`Amount for ${vehicle.name}`}
                            style={{ ...inputStyle, background: '#f8fafc' }}
                            value={row.amount || ''}
                            readOnly
                          />
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Footer: Add Button */}
        <div style={{ padding: '16px', display: 'flex', justifyContent: 'flex-end', borderTop: '1px solid #e4e7ec' }}>
          <ButtonV2
            color="primary"
            style={{ height: '40px', padding: '0 24px', fontSize: '14px', fontWeight: 700, borderRadius: '8px' }}
            disabled={submitting || !origin || !destination || !distance || !transitTime || Object.values(rowData).every((r) => !r.rate || parseFloat(r.rate) <= 0)}
            onClick={handleAdd}
          >
            {submitting ? 'Adding...' : 'Add'}
          </ButtonV2>
        </div>
      </div>
    </div>
  );
};

export default AddListView;
